import type React from 'react';
import { Box, Heading, Image, List } from '@zillow/constellation-10';
import Modal from '@rental-manager/pages/Onboarding/WizardShell/WizardScreen';
import userPersonas from '@common/constants/userPersonas';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

const CHECKLIST_SPOT_ILLUSTRATION =
  'https://delivery.digitallibrary.zillowgroup.com/public/checklist-light_svg_Original.svg';

interface Props {
  onSubmit: () => void;
  onClose: () => void;
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
}

const Checklist: React.FC<Props> = ({ onSubmit, onClose, user, currentProperty }) => {
  const handleSubmit = (): void => {
    IreneOnboardingCSEvents.emitContinueFromChecklistPageEvent({ currentProperty, user });
    return onSubmit();
  };

  const handleClose = (): void => {
    IreneOnboardingCSEvents.emitExitFromOnboardingFlowEvent({
      currentProperty,
      user,
      triggerObject: 'checklist',
    });
    onClose();
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      onRequestClose={handleClose}
      header="Payment setup"
      body={
        <Box>
          <Box aria-hidden="true" css={{ marginBottom: 'default' }}>
            <Image src={CHECKLIST_SPOT_ILLUSTRATION} alt="" width={120} height={120} css={{ display: 'block' }} />
          </Box>
          <Heading level={3} textStyle="heading-xs" css={{ marginBottom: 'tightest' }}>
            Here’s what you’ll need
          </Heading>
          <List.Root appearance="bullet">
            <List.Item>Bank account info for where you’d like to receive payments</List.Item>
            <List.Item>Amounts for the monthly rent and any move-in charges</List.Item>
            <List.Item>Basic details about your renter</List.Item>
            <List.Item>
              Your Social Security number (SSN), a physical mailing address, and an employer identification number (EIN)
              if your rental is owned by a business
            </List.Item>
          </List.Root>
        </Box>
      }
      footerEnd={<DebouncedButton onClick={handleSubmit}>Continue</DebouncedButton>}
    />
  );
};

export default Checklist;
